/**
 * GrandfatherBanner — top-level banner pokazujący okres przejściowy
 * planu PRO dla wszystkich tenantów (do 13.06.2026).
 *
 * Wyświetlany przez ~20 ostatnich dni przed końcem grandfather period.
 * Dismissable per sesja (localStorage).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, ArrowRight, X, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { getMyPlan } from '../../lib/api';


const DISMISS_KEY = 'maskil_grandfather_banner_dismissed_at';
const REDISMISS_AFTER_MS = 24 * 60 * 60 * 1000;  // 24h


export default function GrandfatherBanner({ lang = 'pl' }) {
  const [myPlan, setMyPlan] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check localStorage for dismiss
    try {
      const lastDismiss = localStorage.getItem(DISMISS_KEY);
      if (lastDismiss) {
        const age = Date.now() - parseInt(lastDismiss, 10);
        if (age < REDISMISS_AFTER_MS) {
          setDismissed(true);
          return;
        }
      }
    } catch { /* silent */ }

    // Fetch my-plan
    getMyPlan().then(setMyPlan).catch(() => {});
  }, []);

  if (dismissed || !myPlan) return null;
  if (!myPlan.grandfather_active) return null;

  // Compute days remaining
  let daysLeft = null;
  try {
    const until = new Date(myPlan.grandfather_until);
    daysLeft = Math.ceil((until - new Date()) / (1000 * 60 * 60 * 24));
  } catch { /* */ }

  const urgent = daysLeft !== null && daysLeft <= 7;

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch { /* */ }
  }

  return (
    <div
      className={`relative px-4 py-2 text-xs flex items-center justify-center gap-3 flex-wrap ${
        urgent
          ? 'bg-amber-500/15 border-b border-amber-500/30 text-amber-200'
          : 'bg-emerald-500/10 border-b border-emerald-500/30 text-emerald-200'
      }`}
      data-testid="grandfather-banner"
    >
      {urgent ? (
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
      ) : (
        <Gift className="w-4 h-4 text-emerald-400 flex-shrink-0" />
      )}
      <span className="flex-1 text-center">
        {urgent ? (
          lang === 'pl'
            ? <>⚠ Okres przejściowy kończy się za <strong>{daysLeft} dni</strong> — po 13.06.2026 wymagany upgrade dla funkcji PRO (Inwestycje, D3 Zmiany SWZ, Daily Digest, Auto-discovery).</>
            : <>⚠ Grandfather period ends in <strong>{daysLeft} days</strong> — upgrade required for PRO features after 13.06.2026.</>
        ) : (
          lang === 'pl'
            ? <>🎁 Okres przejściowy aktywny do <strong>13.06.2026</strong> — wszystkie funkcje PRO dostępne w cenie Startera{daysLeft !== null && ` (zostało ${daysLeft} dni)`}.</>
            : <>🎁 Grandfather period active until <strong>13.06.2026</strong> — all PRO features available at Starter price{daysLeft !== null && ` (${daysLeft} days left)`}.</>
        )}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/plans')}
        className={`h-6 px-2 text-xs gap-1 ${
          urgent ? 'text-amber-300 hover:bg-amber-500/20' : 'text-emerald-300 hover:bg-emerald-500/20'
        }`}
      >
        {lang === 'pl' ? 'Zobacz plany' : 'View plans'}
        <ArrowRight className="w-3 h-3" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={dismiss}
        className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}
